import { MessageSquare, Pencil } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';

import { useCareStateCommands } from '@/entities/care-state-domain';
import { useUserInfoQuery } from '@/entities/user-info';
import { useWorkflowsAvailableQuery } from '@/entities/workflows-available';
import { CareStateScopeTree } from '@/features/care-state-scope-tree';
import {
  ActionChip,
  MessageComposer,
  OrdersChipMenu,
  QuickActions,
  type CopilotComposerMode,
} from '@/features/copilot';
import {
  $copilotOverlayIds,
  closeCopilotOverlay,
  openCopilotOverlay,
  setCopilotActiveTab,
} from '@/shared/stores/ui';

import { CopilotOverlayOutlet } from './CopilotOverlayOutlet';

interface CopilotDockProps {
  userId: string;
  effectiveComposerMode: CopilotComposerMode;
  isBusy: boolean;
  showQuickActions?: boolean;
  onStop: () => void;
  onSendMessage: (message: string) => void;
}

/**
 * CopilotDock — floating bottom dock for the copilot panel.
 *
 * Owns rendering of the copilot composer so panel composition stays minimal.
 */
export function CopilotDock(_props: CopilotDockProps) {
  const activeOverlayIds = useStore($copilotOverlayIds);
  const commands = useCareStateCommands();
  const dockRef = useRef<HTMLDivElement>(null);
  const {
    userId,
    effectiveComposerMode,
    isBusy,
    showQuickActions = false,
    onStop,
    onSendMessage,
  } = _props;

  // Warm the query cache for downstream consumers.
  useUserInfoQuery(userId);

  const workflowsQuery = useWorkflowsAvailableQuery(userId);
  const workflowsLoading = workflowsQuery.isLoading;
  const canOrderLabs = workflowsQuery.data?.canOrderLabs ?? false;
  const canPrescribe = workflowsQuery.data?.canWritePrescription ?? false;

  const isInternalNotesMode = effectiveComposerMode === 'internalNotes';
  const isFollowUpsOverlayOpen = activeOverlayIds.includes('followUps');
  const composerIsBusy = isInternalNotesMode ? false : isBusy;

  useEffect(() => {
    const el = dockRef.current;
    if (!el) return;

    const updateHeight = () => {
      const height = el.offsetHeight;
      el.parentElement?.style.setProperty('--dock-height', `${height}px`);
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSend = (text: string) => {
    if (isInternalNotesMode) {
      commands.execute({ type: 'internalNote.create', note: text });
      return;
    }
    onSendMessage(text);
    if (isFollowUpsOverlayOpen) {
      closeCopilotOverlay('followUps');
    }
  };

  return (
    <div ref={dockRef} className="absolute inset-x-0 bottom-0 z-10">
      {/* Gradient fade extends above dock — not counted in --dock-height */}
      <div className="pointer-events-none absolute inset-x-0 bottom-full h-16 bg-gradient-to-t from-background to-transparent" />

      <div className="bg-background px-2 pt-4 pb-2">
        {showQuickActions ? <QuickActions className="mb-3" onAction={handleSend} /> : null}
        <div className="-mb-1.5 px-1">
          <CareStateScopeTree />
        </div>
        <MessageComposer
          className="relative z-10"
          isBusy={composerIsBusy}
          onStop={composerIsBusy ? onStop : undefined}
          onSend={handleSend}
          placeholder={isInternalNotesMode ? 'Write an internal note' : undefined}
          leftActions={
            <>
              <OrdersChipMenu
                disabled={false}
                loading={workflowsLoading}
                hasInsurance={canOrderLabs}
                canPrescribe={canPrescribe}
                onPrescriptionClick={() => openCopilotOverlay('rxWizard')}
                onLabClick={() => openCopilotOverlay('labOrderWizard')}
              />
              <ActionChip
                icon={MessageSquare}
                label="Follow Ups"
                showLabel
                selected={false}
                onClick={() => {
                  if (isFollowUpsOverlayOpen) {
                    return;
                  }
                  openCopilotOverlay('followUps');
                }}
                disabled={false}
              />
              <ActionChip
                icon={Pencil}
                label="Internal Notes"
                showLabel
                selected={isInternalNotesMode}
                onClick={() => setCopilotActiveTab(isInternalNotesMode ? 'copilot' : 'notepad')}
                disabled={false}
              />
            </>
          }
        />
      </div>

      <CopilotOverlayOutlet
        activeOverlayIds={activeOverlayIds}
        userId={userId}
        onClose={closeCopilotOverlay}
      />
    </div>
  );
}
